// frontend/src/components/admin/SeasonDatesPanel.tsx
//
// Admin panel for season dates + split-season boundaries (SPLIT-SEASON.md §4).
// Closes the long-standing gap where /admin/fantasy-season/{id}/dates existed
// with no UI. Sets the four dates, triggers the skeleton recompute, and
// surfaces the blank-week playoff warnings the endpoint returns.

import React, { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/api/auth';
import {
  AdminFantasyWeekCalendar,
  fetchFantasyWeekCalendarAdmin,
  setFantasyWeekCalendarStateAdmin,
  setSeasonDatesAdmin,
  SeasonDatesResult,
} from '@/api/admin';
import { useConfirmDialog } from '@/components/common/ConfirmDialog';

const inputClass = 'w-full p-2 rounded-lg bg-primary-dark border border-gray-600 text-white focus:outline-none focus:border-accent-yellow';
const labelClass = 'block text-xs font-medium text-gray-400 mb-1';

const SeasonDatesPanel: React.FC = () => {
  const { token } = useAuth();
  const confirm = useConfirmDialog();
  const [fsdId, setFsdId] = useState('');
  const [seasonStart, setSeasonStart] = useState('');
  const [seasonEnd, setSeasonEnd] = useState('');
  const [fallEnd, setFallEnd] = useState('');
  const [springStart, setSpringStart] = useState('');
  const [springEnd, setSpringEnd] = useState('');
  // Blank = keep the stored value, so clearing a stored boundary needs an
  // explicit affordance (sends a literal null; date input is ignored).
  const [clearFallEnd, setClearFallEnd] = useState(false);
  const [clearSpringStart, setClearSpringStart] = useState(false);
  const [clearSpringEnd, setClearSpringEnd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<SeasonDatesResult | null>(null);
  const [calendar, setCalendar] = useState<AdminFantasyWeekCalendar | null>(null);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  const [updatingWeek, setUpdatingWeek] = useState<number | null>(null);
  const calendarRequestVersion = useRef(0);
  const [breakReason, setBreakReason] = useState<'INTERNATIONAL_BREAK' | 'WINTER_BREAK' | 'OTHER_NO_FIXTURES'>('INTERNATIONAL_BREAK');

  const canSubmit = !!token && !!parseInt(fsdId, 10) && !!seasonStart && !!seasonEnd && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || !token) return;
    setIsSubmitting(true);
    setResult(null);
    try {
      // OMIT blank boundary fields (undefined keys drop out of the JSON body,
      // so the backend's __fields_set__ guard leaves the stored values alone).
      // Blank means "keep", never "wipe" - wiping is the explicit per-field
      // "clear" checkbox, which sends a literal null.
      const res = await setSeasonDatesAdmin(token, parseInt(fsdId, 10), {
        season_start_calendar_date: seasonStart,
        season_end_calendar_date: seasonEnd,
        ...(clearFallEnd ? { fall_season_end_date: null } : fallEnd ? { fall_season_end_date: fallEnd } : {}),
        ...(clearSpringStart ? { spring_season_start_date: null } : springStart ? { spring_season_start_date: springStart } : {}),
        ...(clearSpringEnd ? { spring_season_end_date: null } : springEnd ? { spring_season_end_date: springEnd } : {}),
      });
      setResult(res);
      setCalendar(null);
      if (res.warnings && res.warnings.length > 0) {
        toast.error(`Saved with ${res.warnings.length} warning(s) - review below.`);
      } else {
        toast.success('Season dates saved; skeleton recomputed.');
      }
    } catch (err: any) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to set season dates.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadCalendar = async (targetFsdId?: number) => {
    const parsedFsdId = targetFsdId ?? parseInt(fsdId, 10);
    if (!token || !parsedFsdId) return;
    const requestVersion = ++calendarRequestVersion.current;
    setIsLoadingCalendar(true);
    try {
      const loaded = await fetchFantasyWeekCalendarAdmin(token, parsedFsdId);
      if (calendarRequestVersion.current === requestVersion) setCalendar(loaded);
    } catch (err: unknown) {
      if (calendarRequestVersion.current === requestVersion) {
        toast.error(err instanceof ApiError ? err.message : 'Failed to load fantasy weeks.');
      }
    } finally {
      if (calendarRequestVersion.current === requestVersion) setIsLoadingCalendar(false);
    }
  };

  const updateCalendarWeek = async (weekNumber: number, state: 'BREAK' | 'CLEAR') => {
    const loadedCalendar = calendar;
    const row = loadedCalendar?.weeks.find(week => week.fantasy_week_number === weekNumber);
    if (!token || !row || !loadedCalendar) return;
    const requestVersion = calendarRequestVersion.current;
    const reasonLabel = {
      INTERNATIONAL_BREAK: 'International Break',
      WINTER_BREAK: 'Winter Break',
      OTHER_NO_FIXTURES: 'No League Matches',
    }[breakReason];
    const accepted = await confirm({
      title: state === 'BREAK' ? 'Confirm No-Contest Week' : 'Clear No-Contest Week',
      message: state === 'BREAK'
        ? `Confirm ${loadedCalendar.competition_name} ${loadedCalendar.season_identifier}, Week ${weekNumber} (${row.calendar_start_date} to ${row.calendar_end_date}) as "${reasonLabel}"? This affects ${row.affected_league_ids.length} active league(s). Combined leagues remain playable if their other competition has fixtures. Existing opponent schedules will not change.`
        : `Clear the break override for ${loadedCalendar.competition_name} ${loadedCalendar.season_identifier}, Week ${weekNumber} (${row.calendar_start_date} to ${row.calendar_end_date})? Existing opponent schedules will not change.`,
      confirmLabel: state === 'BREAK' ? 'Confirm Break' : 'Clear Break',
      destructive: state === 'CLEAR',
    });
    if (!accepted || calendarRequestVersion.current !== requestVersion) return;
    setUpdatingWeek(weekNumber);
    try {
      await setFantasyWeekCalendarStateAdmin(
        token,
        loadedCalendar.fantasy_season_definition_id,
        weekNumber,
        state,
        breakReason,
      );
      toast.success(
        state === 'BREAK'
          ? 'No-contest week confirmed.'
          : 'No-contest week cleared.',
      );
      if (calendarRequestVersion.current === requestVersion) {
        await loadCalendar(loadedCalendar.fantasy_season_definition_id);
      }
    } catch (err: unknown) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update the fantasy week.');
    } finally {
      setUpdatingWeek(null);
    }
  };

  return (
    <>
      <h2 className="text-2xl font-heading font-bold text-accent-yellow mb-2 text-center">Season Dates & Fantasy-Season Boundaries</h2>
      <p className="text-center text-gray-400 text-sm mb-6 max-w-2xl mx-auto">
        Sets a season definition's start/end dates (recomputes the Tue-Mon week skeleton and re-stamps
        matches) plus the split-season boundaries: the <strong>Fall championship week's date</strong> (EPL 2026/27:
        the Boxing Day round), the <strong>Spring first-round date</strong>, and the <strong>Spring championship
        round date</strong> - the PENULTIMATE round (EPL: GW37), never the final day, so final-day squad rotation
        can't decide a championship. Week numbers autocalc; leagues snapshot them at creation.
      </p>
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Fantasy Season Definition ID</label>
            <input type="text" inputMode="numeric" value={fsdId} onChange={e => {
              calendarRequestVersion.current += 1;
              setFsdId(e.target.value.replace(/[^0-9]/g, ''));
              setCalendar(null);
              setIsLoadingCalendar(false);
            }} disabled={updatingWeek !== null} placeholder="e.g. 5" className={inputClass} />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => void loadCalendar()}
              disabled={!token || !parseInt(fsdId, 10) || isLoadingCalendar}
              className="min-h-11 w-full rounded-lg border border-gray-600 px-4 text-sm font-bold text-gray-200 transition-colors hover:border-accent-yellow hover:text-accent-yellow disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoadingCalendar ? 'Loading calendar...' : 'Review Fantasy Weeks'}
            </button>
          </div>
          <div>
            <label className={labelClass}>Season Start (Fantasy Week 1)</label>
            <input type="date" value={seasonStart} onChange={e => setSeasonStart(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Season End (final round)</label>
            <input type="date" value={seasonEnd} onChange={e => setSeasonEnd(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fall Season End (championship round date) - blank keeps current</label>
            <input type="date" value={fallEnd} onChange={e => setFallEnd(e.target.value)} disabled={clearFallEnd} className={inputClass} />
            <label className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <input type="checkbox" checked={clearFallEnd} onChange={e => setClearFallEnd(e.target.checked)} />
              Clear the stored date
            </label>
          </div>
          <div>
            <label className={labelClass}>Spring Season Start (first round date) - blank keeps current</label>
            <input type="date" value={springStart} onChange={e => setSpringStart(e.target.value)} disabled={clearSpringStart} className={inputClass} />
            <label className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <input type="checkbox" checked={clearSpringStart} onChange={e => setClearSpringStart(e.target.checked)} />
              Clear the stored date
            </label>
          </div>
          <div>
            <label className={labelClass}>Spring Season End (championship round date, GW37 - never the final day) - blank keeps current</label>
            <input type="date" value={springEnd} onChange={e => setSpringEnd(e.target.value)} disabled={clearSpringEnd} className={inputClass} />
            <label className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <input type="checkbox" checked={clearSpringEnd} onChange={e => setClearSpringEnd(e.target.checked)} />
              Clear the stored date
            </label>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full py-3 rounded-xl font-heading font-bold text-base bg-accent-yellow text-primary-dark hover:bg-accent-yellow/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving + recomputing skeleton...' : 'Save Season Dates'}
        </button>
        {result && (
          <div className="text-sm bg-primary-dark border border-gray-700 rounded-lg p-3 space-y-1">
            <p className="text-gray-300">Recompute: <span className="text-accent-light-blue">{result.status}</span>{typeof result.weeks === 'number' ? ` - ${result.weeks} fantasy weeks` : ''}</p>
            {result.warnings && result.warnings.length > 0 && (
              <ul className="list-disc pl-5 text-red-400">
                {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            {result.warnings && result.warnings.length === 0 && (
              <p className="text-green-400">No blank-week warnings - all championship-window weeks have fixtures.</p>
            )}
          </div>
        )}
        {calendar && (
          <section className="rounded-xl border border-gray-700 bg-primary-dark" aria-labelledby="calendar-weeks-heading">
            <div className="border-b border-gray-700 p-4">
              <h3 id="calendar-weeks-heading" className="font-heading text-lg font-bold text-accent-yellow">
                No-Contest Weeks
              </h3>
              <p className="mt-1 text-xs text-gray-400">
                {calendar.competition_name} · {calendar.season_identifier} · Definition #{calendar.fantasy_season_definition_id}. A zero-fixture week remains unknown until you confirm it here.
              </p>
              <label className="mt-3 block max-w-xs text-xs font-semibold text-gray-300">
                Reason shown to managers
                <select
                  value={breakReason}
                  onChange={event => setBreakReason(event.target.value as typeof breakReason)}
                  className={`${inputClass} mt-1`}
                >
                  <option value="INTERNATIONAL_BREAK">International Break</option>
                  <option value="WINTER_BREAK">Winter Break</option>
                  <option value="OTHER_NO_FIXTURES">No League Matches</option>
                </select>
              </label>
            </div>
            <div className="max-h-96 divide-y divide-gray-800 overflow-y-auto">
              {calendar.weeks.map(week => {
                const stateTone = week.state === 'ACTIVE'
                  ? 'text-green-400'
                  : week.state === 'BREAK'
                    ? 'text-accent-yellow'
                    : 'text-gray-400';
                return (
                  <div key={week.fantasy_week_number} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white">
                        Week {week.fantasy_week_number}
                        <span className="ml-2 font-normal text-gray-500">
                          {week.calendar_start_date} to {week.calendar_end_date}
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {week.fixture_count} fixture{week.fixture_count === 1 ? '' : 's'} ·{' '}
                        <span className={`font-bold ${stateTone}`}>{week.state}</span>
                        {week.conflict ? ` · ${week.conflict.replace(/_/g, ' ')}` : ''}
                      </p>
                      {week.playoff_conflict_league_ids.length > 0 && (
                        <p className="mt-1 text-xs font-semibold text-red-400">
                          Potential playoff conflict if every member is confirmed off: league {week.playoff_conflict_league_ids.join(', ')}
                        </p>
                      )}
                    </div>
                    {week.confirmed_at ? (
                      <button
                        type="button"
                        onClick={() => updateCalendarWeek(week.fantasy_week_number, 'CLEAR')}
                        disabled={updatingWeek !== null}
                        className="min-h-11 shrink-0 rounded-lg border border-gray-600 px-4 text-xs font-bold text-gray-200 hover:border-red-400 hover:text-red-300 disabled:opacity-50"
                      >
                        {updatingWeek === week.fantasy_week_number ? 'Updating...' : 'Clear Break'}
                      </button>
                    ) : week.fixture_count === 0 ? (
                      <button
                        type="button"
                        onClick={() => updateCalendarWeek(week.fantasy_week_number, 'BREAK')}
                        disabled={updatingWeek !== null}
                        className="min-h-11 shrink-0 rounded-lg bg-accent-yellow px-4 text-xs font-bold text-primary-dark hover:bg-accent-yellow/80 disabled:opacity-50"
                      >
                        {updatingWeek === week.fantasy_week_number ? 'Updating...' : 'Confirm International Break'}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default SeasonDatesPanel;
